
import { LayoutDashboard, Search, FileText, Crown } from "lucide-react";
import {
  Sidebar,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useSubscription } from "@/hooks/useSubscription";
import { cn } from "@/lib/utils";
import CustomTooltip from "@/components/ui/custom-tooltip";

const menuItems = [
  {
    title: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
    requiresSubscription: true,
    subscriptionKey: "can_view_dashboard",
  },
  {
    title: "Competitors Dashboard",
    icon: LayoutDashboard,
    path: "/competitors-dashboard",
    requiresSubscription: true,
    subscriptionKey: "can_view_competitors_dashboard",
  },
  {
    title: "Product Search",
    icon: Search,
    path: "/",
    requiresSubscription: false,
  },
  {
    title: "Uploaded Files",
    icon: FileText,
    path: "/uploaded-files",
    requiresSubscription: false,
  },
];

const checkSubscriptionAccess = (subscription: any, key?: string) => {
  const accessMap: Record<string, () => boolean> = {
    can_view_dashboard: subscription.canViewDashboard,
    can_view_competitors_dashboard: subscription.canViewCompetitorsDashboard,
  };

  return key ? accessMap[key]?.() ?? true : true;
};

export const AppSidebar = () => {
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const subscription = useSubscription();

  if (!isAuthenticated) return null;

  return (
    <Sidebar collapsible="icon" className="bg-white/95 dark:bg-gray-900/95">
      <div className="flex flex-col h-full py-4">
        <SidebarMenu>
          {menuItems.map((item) => {
            const isLocked =
              item.requiresSubscription &&
              !checkSubscriptionAccess(subscription, item.subscriptionKey);

            const menuLink = (
              <Link
                to={isLocked ? "#" : item.path}
                className={cn(
                  "flex items-center w-full gap-2 px-3 py-2 rounded-md text-sm",
                  location.pathname === item.path
                    ? "bg-primary/10 text-primary hover:bg-primary/15 font-medium"
                    : "text-slate-700 hover:bg-slate-100/80 dark:text-slate-200 dark:hover:bg-slate-800/70",
                  isLocked &&
                    "opacity-50 cursor-not-allowed"
                )}
                onClick={(e) => isLocked && e.preventDefault()}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.title}</span>
                {isLocked && <Crown className="h-4 w-4 text-yellow-500" />}
              </Link>
            );

            return (
              <SidebarMenuItem key={item.title}>
                {isLocked ? (
                  <CustomTooltip content="Upgrade your plan to access this feature">
                    {menuLink}
                  </CustomTooltip>
                ) : (
                  menuLink
                )}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </div>
    </Sidebar>
  );
};
